// Hotel Email Service - Adapts hotel data to use existing flight email service
import axios from 'axios';

// Constants - Use your existing AWS Lambda endpoints
const MAIN_LAMBDA = process.env.REACT_APP_MAIN_LAMBDA || "https://7p06sk4uo1.execute-api.eu-central-1.amazonaws.com/dev";
const BOOKINGS_TABLE = "bookings"; // Your existing DynamoDB table

/**
 * Transform hotel booking data to match flight booking format expected by webhookListener
 */
const transformHotelDataForEmailService = (hotelBookingData) => {
  const {
    reservationNumber,
    hotelName,
    city,
    country,
    checkIn,
    checkOut,
    guests,
    totalPrice,
    email,
    firstName,
    lastName,
    isTestBooking = false,
    transactionId
  } = hotelBookingData;

  // Generate a unique booking ID for hotels (similar to flight bookingId)
  const hotelBookingId = reservationNumber ? 
    reservationNumber.replace(/[^0-9]/g, '').slice(0, 8) : 
    Math.floor(Math.random() * 100000000);

  // Transform hotel data to match flight booking structure
  return {
    data: {
      bookingId: parseInt(hotelBookingId),
      orderNum: reservationNumber || `HTL${hotelBookingId}`,
      // Add any other properties the email service might need
      totalPrice: totalPrice?.amount || 0,
      currency: totalPrice?.currency || 'EUR'
    },
    // Required fields for email service
    email: email,
    name: firstName || 'Guest',
    lastName: lastName || '',
    from_destination: city || 'Hotel Location',
    to_destination: hotelName || 'Hotel Booking',
    booking_token: transactionId || `hotel_${Date.now()}`,
    
    // Hotel-specific data (for potential future use)
    hotelData: {
      reservationNumber,
      hotelName,
      city,
      country,
      checkIn,
      checkOut,
      guests,
      totalPrice,
      isTestBooking
    }
  };
};

/**
 * Save hotel booking to DynamoDB (using existing table structure)
 */
export const saveHotelBookingForEmail = async (hotelBookingData) => {
  try {
    
    // Transform hotel data to flight-compatible format
    const transformedData = transformHotelDataForEmailService(hotelBookingData);
    

    // Save to DynamoDB using existing flight booking endpoint
    const saveResponse = await axios.post(
      `${MAIN_LAMBDA}/saveBookingResponse`,
      transformedData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    
    return {
      success: true,
      bookingId: transformedData.data.bookingId,
      transformedData
    };
    
  } catch (error) {
    console.error('❌ Error saving hotel booking:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Trigger hotel confirmation email using existing flight email service
 */
export const sendHotelConfirmationEmail = async (hotelBookingData) => {
  try {
    
    // First save the booking data
    const saveResult = await saveHotelBookingForEmail(hotelBookingData);
    
    if (!saveResult.success) {
      throw new Error('Failed to save hotel booking data');
    }

    // Transform hotel data for email service
    const emailData = transformHotelDataForEmailService(hotelBookingData);
    
    // Trigger email using existing webhookListener endpoint
    const emailResponse = await axios.post(
      `${MAIN_LAMBDA}/webhookListener`,
      emailData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    
    return {
      success: true,
      emailResponse: emailResponse.data,
      bookingId: saveResult.bookingId
    };
    
  } catch (error) {
    console.error('❌ Error sending hotel email:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Send hotel booking status email (pending/confirmed/cancelled)
 */
export const sendHotelStatusEmail = async (hotelBookingData, status = 'confirmed') => {
  try {
    
    const emailData = transformHotelDataForEmailService({
      ...hotelBookingData,
      bookingStatus: status
    });
    
    // The existing email service will automatically handle different statuses
    return await sendHotelConfirmationEmail(emailData);
    
  } catch (error) {
    console.error(`❌ Error sending hotel ${status} email:`, error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Utility function to get hotel booking by ID from DynamoDB
 */
export const getHotelBookingById = async (bookingId) => {
  try {
    const response = await axios.get(
      `${MAIN_LAMBDA}/getBookingById?booking_id=${bookingId}`,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      success: true,
      booking: response.data
    };
    
  } catch (error) {
    console.error('❌ Error getting hotel booking:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Example usage:
/*
const hotelBookingExample = {
  reservationNumber: "HTL123456",
  hotelName: "Grand Hotel Example",
  city: "Pristina",
  country: "Kosovo",
  checkIn: "2024-01-15",
  checkOut: "2024-01-17",
  email: "guest@example.com",
  firstName: "John",
  lastName: "Doe",
  totalPrice: {
    amount: 250,
    currency: "EUR"
  },
  guests: [
    { name: "John Doe", nationality: "US" }
  ],
  transactionId: "txn_hotel_123"
};

// Send confirmation email
await sendHotelConfirmationEmail(hotelBookingExample);
*/ 